import { TabItem, Tabs } from '@hai/ui-react';
import { isNil } from 'ramda';
import React, { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, matchPath, useNavigate } from 'react-router-dom';
import PageHeader from 'src/js/component/page-header';
import useLocalStorageWithState from 'src/js/component/useLocalStorageWithState';
import systemInfo, { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { compileWithArgs } from 'src/js/helper/path-to-regexp-helper';
import { useNavigateListener } from 'src/js/hook/use-history-listener';
import { useStores } from 'src/js/hook/use-stores';
import { Paths } from 'src/js/route';
import SidebarLayout from 'src/js/sidebar';
import { useWatchObject } from 'src/js/store/use-watch';
import { constant } from 'src/js/util/constant';

export type SettingsParameters = {
  page?: SettingKey;
};

export enum SettingKey {
  SYSTEM = 'system',
  UPGRADE = 'upgrade',
  NETWORK = 'network',
  DATETIME = 'dateTime',
  SERVICES = 'services',
  LICENSING = 'licensing',
  COMPORT = 'comport',
}

const AdminTabs = Object.values(SettingKey);
const NonAdminTabs = ['system'];

export const Settings: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionStore } = useStores();
  const [selectedTab, setSelectedTab] = useLocalStorageWithState(
    `${constant.lStorage.settingsTab}:${sessionStore.username}`,
    SettingKey.SYSTEM,
  );

  const [tabs, setTabs] = useState<string[]>([]);
  const updateTabs = () => {
    let tabs = sessionStore.isAdmin() ? AdminTabs : NonAdminTabs;
    if (!systemInfoCapabilities.isRugged()) {
      tabs = tabs.filter((v) => v !== SettingKey.COMPORT);
    }

    setTabs(tabs);
  };

  useEffect(() => {
    updateTabs();
  }, []);
  useWatchObject(systemInfo, updateTabs);

  useNavigateListener((newLocation) => {
    const routeName = '/settings/:page';
    const matchedParam = matchPath(routeName, newLocation.pathname);
    // NKE-2600 Prevent people from directly accessing tabs that are currently unavailable (or from redirect after login)
    if (!isNil(matchedParam?.params) && !isNil(tabs.find((t) => t === matchedParam.params?.page))) {
      setSelectedTab(matchedParam.params.page as SettingKey);
    }
  });

  return (
    <SidebarLayout>
      <PageHeader title={t('sidebar.settings')} iconName="Settings" />
      <Tabs
        selectedTab={selectedTab}
        onSelect={(event: ChangeEvent<{ eventkey: SettingKey }>) => {
          setSelectedTab(event.target.eventkey);
          navigate(
            compileWithArgs<SettingsParameters>(Paths.settings)({
              page: event.target.eventkey,
            }),
          );
        }}
      >
        {tabs.map(
          (tab): ReactNode => (
            <TabItem key={tab} caption={t(`settings.${tab}.title`)} eventkey={tab} />
          ),
        )}
      </Tabs>
      <Outlet />
    </SidebarLayout>
  );
};
